// Minimal API-klient. Cookies flödar automatiskt via same-origin (Vite proxar
// /api i dev, nginx i prod). CSRF-token hämtas från /api/auth/csrf och skickas
// i X-CSRF-TOKEN på alla mutations — servern kräver den på POST/PUT/DELETE.
//
// Efter login/logout måste CSRF-token hämtas igen — token är identitetsbunden.

let cachedCsrfToken: string | null = null

async function fetchCsrfToken(): Promise<string> {
  const res = await fetch('/api/auth/csrf', { credentials: 'same-origin' })
  if (!res.ok) throw new Error(`CSRF hämtning misslyckades: ${res.status}`)
  const data = await res.json()
  cachedCsrfToken = data.token
  return data.token
}

export async function getCsrfToken(): Promise<string> {
  if (cachedCsrfToken) return cachedCsrfToken
  return fetchCsrfToken()
}

export function invalidateCsrfToken(): void {
  cachedCsrfToken = null
}

interface RequestOptions {
  method?: string
  body?: unknown
  // Sätt till true på fetch-anrop som INTE ska mutera CSRF-cache
  // (t.ex. logout — där rensar vi cachen efteråt).
  csrf?: boolean
}

export class ApiError extends Error {
  status: number
  body: unknown
  constructor(status: number, body: unknown) {
    super(`HTTP ${status}`)
    this.status = status
    this.body = body
  }
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET'
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }

  const needsCsrf = opts.csrf ?? (method !== 'GET' && method !== 'HEAD')
  if (needsCsrf) headers['X-CSRF-TOKEN'] = await getCsrfToken()

  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  })

  if (!res.ok) {
    let body: unknown = null
    try { body = await res.json() } catch { /* text response */ }
    throw new ApiError(res.status, body)
  }

  const contentType = res.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) return (await res.json()) as T
  return undefined as T
}
